import { Fragment, useEffect, useReducer, useState, type ReactNode } from 'react';
import { ExternalLink } from 'lucide-react';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { TextButton } from '../../ui/TextButton';
import { Typography } from '../../ui/Typography';
import { liveChoiceOptions, type ChoiceOption, type MessageChoices } from '../../../../lib/message-choices';
import { useTranslation } from '../../../../lib/i18n';
import { finishMeetingConnect, startMeetingConnect, type MeetingConnectDeps, type MeetingConnectOutcome } from '../../../../lib/meeting-connect-flow';
import { meetingChoiceChip, meetingJoinChipView, nextMeetingJoinChange } from '../../../../lib/meeting-join-chip';
import {
  meetingNotice,
  meetingStatus,
  useFreshMeetingsSchedule,
  useMeetingsChannelActions,
  useUpcomingMeeting,
  type UpcomingMeeting,
} from '../../../../lib/meetings-channel';
import { MeetingsCalendarModal } from '../../connectors/MeetingsCalendarModal';
import { useAgentName } from '../../../../lib/use-agent-name';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { CHAT_HOME_BI } from '../message-utils';

// Choice chips — a structured ask's options as pill buttons under the assistant
// body. Selection derives from the transcript (a USER row carrying a matching
// choiceAnswer), so an answered chip gets the SELECTED treatment — but every chip
// stays tappable: another tap is another turn, and free text is always valid. No
// handler (read-only surface) renders the pills inert.
// The one closed case: a SUPERSEDED ask — an older unanswered card the agent has since re-asked
// (`supersededChoiceAskIds`) — is handed no handler, so it draws inert with no selection and no status.
//
// The pill is the DS Light skin; the one override is a Default border. The frame sits the white pill on a grey
// chat surface, but THIS chat surface is white, so a borderless white pill would vanish into it — hence
// the light `--ds-border-light-default` hairline for definition (the same reason SuggestedTaskChips keeps
// an outline here). The phones draw the pill borderless, because their chat surface is the grey #F3F3F3
// the frame assumes. Stated as a deviation in native's ChoiceChips too.
//
// An option carrying `url` is a LINK chip (e.g. "Join this meeting"): it renders as the same pill as a
// real anchor that opens the URL in a new tab, same as the meeting-notes card's own link. It never calls
// `onSelect` — a tap sends nothing, so it never reads as answered and never gets the selected look.
// `liveChoiceOptions` drops anything past its `expiresAt` at render time; a row whose options all
// drop that way renders no chip row at all.
//
// Two exceptions, both read by `meetingChoiceChip` (`lib/meeting-join-chip.ts`): a meeting thread's "Join
// this meeting" chip, which `MeetingJoinChip` draws from the meeting's live row, and the connect chip
// `JoinMeeting` offers an owner who has not connected Meetings, which `MeetingConnectChip` runs.
export function ChoiceChips({
  choices,
  selectedIds,
  onSelect,
  afterFirst,
}: {
  choices: MessageChoices;
  selectedIds: ReadonlySet<string>;
  onSelect?: (option: ChoiceOption) => void;
  afterFirst?: ReactNode;
}) {
  const { t } = useTranslation();
  const options = liveChoiceOptions(choices.options);
  if (options.length === 0) return null;
  const meetingChip = meetingChoiceChip(options);
  if (meetingChip.kind === 'join') {
    return <MeetingJoinChip eventId={meetingChip.eventId} url={meetingChip.option.url!} label={meetingChip.option.label} />;
  }
  if (meetingChip.kind === 'connect') {
    return <MeetingConnectChip eventId={meetingChip.eventId} label={meetingChip.option.label} inert={!onSelect} />;
  }
  return (
    <div role="group" aria-label={t('chat.choices.groupLabel')} style={CHIPS_ROW}>
      {options.map((option, index) => (
        <Fragment key={option.id}>
          {option.url ? (
            <ChoiceLinkChip url={option.url} label={option.label} />
          ) : (
            <ChoiceChip option={option} selected={selectedIds.has(option.id)} onSelect={onSelect} />
          )}
          {index === 0 && afterFirst}
        </Fragment>
      ))}
    </div>
  );
}

// A LINK option renders as the same pill via a real anchor. The props are SPREAD (not passed as JSX
// literals) so `style` AND `dir` type-check: the href branch resolves to ButtonAsLink, which declares
// neither (the DS Button forwards both to its <a> at runtime) — and excess-property checks fire only on
// object literals, not spreads. Same pattern as SuggestedTaskChips' ChipButton. `dir="auto"` rides the
// ANCHOR (not just the label) so the inline-flex row — label + trailing external-link glyph — follows
// the label's own direction (an RTL label in an LTR app keeps the glyph on the correct side; see
// .claude/rules/orion-web-chat.md). It never reads as selected and sends nothing on tap.
function ChoiceLinkChip({ url, label, onClick }: { url: string; label: string; onClick?: () => void }) {
  const props = {
    variant: 'contained' as const,
    color: 'light' as const,
    size: 'medium' as const,
    href: url,
    external: true,
    target: '_blank',
    rel: 'noopener noreferrer',
    dir: 'auto',
    style: PILL,
    endIcon: <ExternalLink size={16} aria-hidden />,
    onClick,
  };
  return <Button {...props}>{label}</Button>;
}

// Before the meeting the chip sends her and opens nothing; from a minute before the start it is the link,
// and a tap sends her too when she is not coming. Beside it, her state as the Meetings list's own badge.
// With no live row (still loading, or past the week the list covers) it stays the plain link it always was.
function MeetingJoinChip({ eventId, url, label }: { eventId: string; url: string; label: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const agentName = useAgentName().short.trim();
  const { meeting } = useUpcomingMeeting(eventId);
  const { decide } = useMeetingsChannelActions();
  // The answer's own line when the Join did not go through: failed (with Try Again), or refused and why. Both are HTTP 200.
  const [failure, setFailure] = useState<{ text: string; retry: boolean } | null>(null);
  useRerenderAt(meeting ? nextMeetingJoinChange(meeting, Date.now()) : undefined);

  if (!meeting) {
    return (
      <div role="group" aria-label={t('chat.choices.groupLabel')} style={CHIPS_ROW}>
        <ChoiceLinkChip url={url} label={label} />
      </div>
    );
  }
  const view = meetingJoinChipView(meeting, Date.now());
  if (!view.chip && !view.line) return null;
  const status = meetingStatus(meeting, t, agentName);

  const tap = () => {
    logPageClickBi(
      { ...CHAT_HOME_BI, button_name: 'meeting-join' },
      { additionalFields: { chip: view.chip ?? '', state: meeting.meetingState ?? meeting.state } },
    );
    if (!view.requestsJoin) return;
    setFailure(null);
    decide.mutate(
      { meeting, decision: 'join' },
      {
        onSuccess: (answer) => {
          const notice = meetingNotice('join', answer);
          setFailure(notice ? { text: t(notice.message, { agent: agentName }), retry: notice.retry === true } : null);
        },
        onError: () => setFailure({ text: t('chat.meetingJoin.failed', { agent: agentName }), retry: true }),
      },
    );
  };
  return (
    <div role="group" aria-label={t('chat.choices.groupLabel')} style={JOIN_BLOCK}>
      {view.chip === 'open' && <ChoiceLinkChip url={url} label={label} onClick={tap} />}
      {view.chip === 'send' && (
        <Button variant="contained" color="light" size="medium" dir="auto" style={PILL} loading={decide.isLoading} onClick={tap}>
          {label}
        </Button>
      )}
      {failure ? (
        <MeetingNoticeLine text={failure.text} onRetry={failure.retry ? tap : undefined} />
      ) : (
        view.line && (
          <>
            {status.badge && <Badge role="status" variant={status.badge.variant} label={status.badge.label} />}
            {status.note && (
              <Typography variant="p3" color="secondary" role={status.note.alert ? 'alert' : undefined} style={JOIN_ERROR}>
                {status.note.text}
              </Typography>
            )}
          </>
        )
      )}
    </div>
  );
}

/** A meeting chip's answer line, with Try Again when the answer offers it. Shared by the join chip and the connect chip. */
function MeetingNoticeLine({ text, onRetry }: { text: string; onRetry?: () => void }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  return (
    <>
      <Typography variant="p3" color="secondary" role="status" style={JOIN_ERROR}>
        {text}
      </Typography>
      {onRetry && (
        <TextButton
          size="small"
          onClick={() => {
            // Logged on top of the chip's own tap, which the retry repeats, so retries can be told from first taps.
            logPageClickBi({ ...CHAT_HOME_BI, button_name: 'meeting_chip_retry' });
            onRetry();
          }}
        >
          {t('common.retry')}
        </TextButton>
      )}
    </>
  );
}

// One tap connects Meetings with the first-connect defaults (an empty body: an existing connection is left alone) and asks her into this meeting
// (`lib/meeting-connect-flow.ts`). With no calendar the tap opens the Connectors page's calendar step first,
// and the connect finishes when the calendar lands. Inert where the row has no send handler (a locked channel,
// a superseded ask): a connect is a write, the same as an answer.
function MeetingConnectChip({ eventId, label, inert }: { eventId: string; label: string; inert: boolean }) {
  const { t } = useTranslation();
  const { logPageClickBi, logLoadStart, logLoadFinish } = useGenericBi();
  const agentName = useAgentName().short.trim();
  const readSchedule = useFreshMeetingsSchedule();
  const { connectWith, decide } = useMeetingsChannelActions();
  const [busy, setBusy] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [outcome, setOutcome] = useState<MeetingConnectOutcome | null>(null);
  // Why the join after the connect did not go through, from the answer itself: a refusal names its reason, a failure
  // offers Try Again.
  const [joinNotice, setJoinNotice] = useState<{ text: string; retry: boolean } | null>(null);

  const deps: MeetingConnectDeps<UpcomingMeeting> = {
    readSchedule,
    connect: (patch) => connectWith.mutateAsync(patch),
    decide: async (meeting) => {
      setJoinNotice(null);
      const answer = await decide.mutateAsync({ meeting, decision: 'join' });
      const notice = meetingNotice('join', answer);
      if (notice) {
        setJoinNotice({ text: t(notice.message, { agent: agentName }), retry: notice.retry === true });
        throw new Error(`meeting join ${answer.kind}`);
      }
    },
    now: Date.now,
    connectStarted: () => logLoadStart('meeting-connect'),
    connectFinished: ({ isSuccessful, loadingTimeMs, outcome: result }) =>
      logLoadFinish('meeting-connect', { isSuccessful, loadingTimeMs }, { additionalFields: { outcome: result } }),
  };
  const run = async (step: () => Promise<MeetingConnectOutcome | 'calendar'>) => {
    setBusy(true);
    setOutcome(null);
    const result = await step();
    setBusy(false);
    if (result === 'calendar') setCalendarOpen(true);
    else setOutcome(result);
  };
  const tap = () => {
    if (inert || busy) return;
    logPageClickBi({ ...CHAT_HOME_BI, button_name: 'meeting-connect' });
    void run(() => startMeetingConnect(eventId, deps));
  };
  const finish = () => {
    setCalendarOpen(false);
    void run(() => finishMeetingConnect(eventId, deps));
  };

  const line =
    outcome === 'joined'
      ? t('chat.meetingConnect.done', { agent: agentName })
      : outcome === 'joinFailed'
        ? joinNotice?.text ?? t('chat.meetingConnect.joinFailed', { agent: agentName })
        : outcome === 'connectFailed'
          ? t('chat.meetingConnect.failed')
          : outcome === 'unavailable'
            ? t('chat.meetingConnect.unavailable', { agent: agentName })
            : null;

  return (
    <div role="group" aria-label={t('chat.choices.groupLabel')} style={JOIN_BLOCK}>
      {outcome !== 'joined' && outcome !== 'unavailable' && (
        <Button
          variant="contained"
          color="light"
          size="medium"
          dir="auto"
          style={PILL}
          loading={busy}
          aria-disabled={inert || undefined}
          tabIndex={inert ? -1 : undefined}
          onClick={inert ? undefined : tap}
        >
          {label}
        </Button>
      )}
      {line && <MeetingNoticeLine text={line} onRetry={outcome === 'joinFailed' && joinNotice?.retry ? tap : undefined} />}
      {calendarOpen && (
        <MeetingsCalendarModal
          hasCalendar={false}
          connected={false}
          connecting={busy}
          onClose={() => setCalendarOpen(false)}
          onCalendarConnected={finish}
          onConnect={finish}
        />
      )}
    </div>
  );
}

// The chip changes at two instants with no new data — a minute before the start, and the end — so it
// re-renders itself then.
function useRerenderAt(at: number | undefined) {
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (at === undefined) return;
    const id = setTimeout(rerender, Math.max(0, at - Date.now()));
    return () => clearTimeout(id);
  }, [at]);
}

function ChoiceChip({
  option,
  selected,
  onSelect,
}: {
  option: ChoiceOption;
  selected: boolean;
  onSelect?: (option: ChoiceOption) => void;
}) {
  // Read-only (locked channel — an answer is a channel send, so the chips lock with the composer): keep
  // the enabled Light-skin visual (contained-light has no disabled colour, so `aria-disabled` only sets
  // cursor:not-allowed), but mark it `aria-disabled` and drop it from the tab order + the click handler
  // so a screen-reader user isn't told an inert chip is actionable. Mirrors the phones' locked pills.
  const inert = !onSelect;
  return (
    <Button
      variant="contained"
      color="light"
      size="medium"
      aria-pressed={selected}
      aria-disabled={inert || undefined}
      onClick={inert ? undefined : () => onSelect!(option)}
      tabIndex={inert ? -1 : undefined}
      dir="auto"
      style={{ ...PILL, ...(selected ? PILL_SELECTED : {}) }}
    >
      {option.label}
    </Button>
  );
}

export function ChoiceActionChip({ label, onPress, disabled, testId }: { label: string; onPress: () => void; disabled?: boolean; testId: string }) {
  return (
    <Button variant="contained" color="light" size="medium" disabled={disabled} onClick={onPress} dir="auto" style={PILL} data-testid={testId}>
      {label}
    </Button>
  );
}

const CHIPS_ROW: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8, // Figma Spacing/8 (between chips)
  marginTop: 20, // Figma Spacing/20 — gap from the assistant message to the chips row
  maxWidth: '100%',
};

// The join chip with its status badge on the same row, centred on the chip's height. A narrow pane
// wraps the badge under the chip.
const JOIN_BLOCK: React.CSSProperties = {
  ...CHIPS_ROW,
  alignItems: 'center',
  columnGap: 12,
};

// The failed-send sentence (and the connect chip's outcome) is not a state, so it keeps its own line under the chip.
const JOIN_ERROR: React.CSSProperties = { flexBasis: '100%' };

// The round pill: the DS Light skin supplies the white fill and the #EAE9E9 hover; we override only the
// radius and the definition border (see the header note on why this surface keeps one). The label wraps
// instead of the Button's `nowrap`: a 40-character label (the harness cap) is wider than a phone at a large
// text size. Same rule as the phones' chips.
const PILL: React.CSSProperties = {
  borderRadius: 9999,
  borderColor: 'var(--ds-border-light-default)',
  maxWidth: '100%',
  whiteSpace: 'normal',
  textAlign: 'center',
};

// Selected (Figma 20312-113671): the grey `Light Secondary/Hover` fill + the `--ds-separator-default` hairline,
// the token native uses, so the border matches in both modes. The fill is set here (not left
// to the skin) so it stays grey whether or not the chip is hovered.
const PILL_SELECTED: React.CSSProperties = {
  background: 'var(--ds-fill-light-secondary-hover)',
  borderColor: 'var(--ds-separator-default)',
};
